import { useCallback } from 'react'
import { useTimer } from './useTimer'

export interface UseTimerGuardReturn {
  /**
   * Verifica se o timer está ativo (running, paused ou stopped e visível)
   */
  isTimerActive: boolean

  /**
   * Verifica se deve bloquear uma ação
   * Retorna true se timer está ativo
   */
  shouldBlock: () => boolean

  /**
   * Callback para ser usado quando uma ação precisa ser bloqueada
   * Retorna true se deve bloquear, false caso contrário
   */
  checkAndBlock: (onBlock: () => void) => boolean
}

/**
 * Hook para proteger ações quando o timer está ativo
 * Usado para impedir mudanças de plano durante sessões de estudo
 * Bloqueia quando timer está rodando, pausado ou encerrado mas ainda visível
 */
export const useTimerGuard = (): UseTimerGuardReturn => {
  const { status, isVisible } = useTimer()

  // Timer está ativo se estiver rodando, pausado ou encerrado mas ainda visível
  const isTimerActive =
    status === 'running' || status === 'paused' || (status === 'stopped' && isVisible)

  const shouldBlock = useCallback(() => {
    return isTimerActive
  }, [isTimerActive])

  const checkAndBlock = useCallback(
    (onBlock: () => void): boolean => {
      if (isTimerActive) {
        onBlock()
        return true
      }
      return false
    },
    [isTimerActive]
  )

  return {
    isTimerActive,
    shouldBlock,
    checkAndBlock,
  }
}
